/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Flow Management Service - Simple functions for business logic
 */

import type { Dispatch } from '@reduxjs/toolkit';

import type { CopilotState } from '../data-layer/types';
import { addMessage, setConnectionPrompt } from '../data-store/copilotSlice';
import { safeTimeout } from '../utils/timeoutUtils';

import {
  initiateOnboard,
  saveAccountConfiguration,
  testConnection,
} from './apiService';
import { showError } from './messageService';
import {
  completeAndMoveToStep,
  completeStep,
  setActive,
  setEndpoint,
} from './stateService';

interface ApiServices {
  initiateOnboard?: (payload: unknown) => Promise<unknown>;
  testConnection?: (payload: unknown) => Promise<unknown>;
  saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
}

export const handleGeneralSubmit = (
  dispatch: Dispatch,
  formData: Record<string, unknown>,
  isValid: boolean,
  visitedSteps: string[],
  onShowConnection: () => void
): void => {
  if (!isValid) {
    return;
  }

  const hasVisitedConnection = visitedSteps.includes('Connection');

  if (hasVisitedConnection) {
    completeStep(dispatch, 'General');
    setActive(dispatch, 'Connection');
    onShowConnection();
  } else {
    // Show transition message with loader for 3 seconds
    dispatch(
      addMessage({
        id: `transition-${Date.now()}`,
        type: 'ai_response',
        subType: 'generalToConnection',
        loader: true,
        timestamp: Date.now(),
      })
    );

    safeTimeout(() => {
      completeAndMoveToStep(dispatch, 'General', 'Connection');
      onShowConnection();
    }, 3000);
  }
};

export const handleConnectionSubmit = (
  dispatch: Dispatch,
  formData: Record<string, unknown>,
  isValid: boolean,
  onShowHelper: () => void
): void => {
  if (!isValid) {
    showError(dispatch, 'Please check connection details');

    return;
  }

  // Complete Connection step and show helper prompt
  completeStep(dispatch, 'Connection');
  onShowHelper();
};

export const handleCreateAccountFlow = async (
  services: ApiServices,
  stepName:
    | 'CreateAccount'
    | 'UpdateAccount'
    | 'EnableAccount'
    | 'DisableAccount'
    | 'RemoveAccount',
  createAccountData: Record<string, unknown>,
  endpointKey: number,
  onSuccess: () => void,
  onError: (message: string) => void
): Promise<void> => {
  try {
    await saveAccountConfiguration(
      services,
      stepName,
      createAccountData,
      endpointKey
    );
    onSuccess();
  } catch (error) {
    onError('Failed to save create account configuration');
  }
};

export const handleOnboardingFlow = async (
  dispatch: Dispatch,
  services: ApiServices,
  generalData: Record<string, unknown>,
  connectionData: Record<string, unknown>,
  onSuccess: (endpointKey?: number) => void,
  onError: (message: string) => void,
  userPrompt?: string,
  getState?: () => any
): Promise<void> => {
  try {
    let parsedTemplates = {};
    let appTypeKey = 85;
    let draftType = 0;
    let state: any = null;

    if (getState) {
      state = getState();

      parsedTemplates = state.copilot?.parsedTemplates || {};
      appTypeKey = state.copilot?.appTypeKey || 85;
      draftType = state.copilot?.draftType || 0;
    }

    const result = await initiateOnboard(
      services,
      generalData,
      connectionData,
      parsedTemplates,
      appTypeKey,
      draftType,
      userPrompt,
      state?.copilot?.categoryTemplates
    );

    const endpointKey = result.endpointKey ?? result.data?.endpointKey;

    const isSuccess = !!endpointKey;

    if (isSuccess) {
      // Only call testConnection if initiateOnboard was successful
      try {
        await testConnection(services, generalData);
      } catch (testError) {
        onError('An error occurred while saving. Please try later...');
      }

      if (endpointKey) {
        setEndpoint(dispatch, endpointKey);
      }

      onSuccess(endpointKey);
    } else {
      // Clear connection prompt on failure
      if (dispatch && getState) {
        const isConnPrompt = getState() as { copilot?: CopilotState };
        const existingPrompt = isConnPrompt.copilot?.connectionPrompt || '';

        // Only clear if no previous prompt exists
        if (!existingPrompt) {
          dispatch(setConnectionPrompt(''));
        }
      }

      throw new Error('Initiate onboard failed'); // Let stepHelpUtils handle the error message
    }
  } catch (error) {
    // Clear connection prompt on error
    if (dispatch && getState) {
      const state = getState() as { copilot?: CopilotState };
      const existingPrompt = state.copilot?.connectionPrompt || '';

      // Only clear if no previous prompt exists
      if (!existingPrompt) {
        dispatch(setConnectionPrompt(''));
      }
    }

    throw error; // Let stepHelpUtils handle the error message
  }
};
